import { z } from 'zod';
import { LIMITS } from '../limits.js';

// Re-exported here so server's compat-shim only needs one import path.
export { attachmentSchema } from './attachment.js';
export type { Attachment } from './attachment.js';

export const messageContentSchema = z
  .string()
  .min(1)
  .max(LIMITS.message.contentMax)
  .refine((s) => s.trim().length > 0, { message: 'content_blank' });

export const clientNonceSchema = z
  .string()
  .min(8)
  .max(64)
  .regex(/^[A-Za-z0-9_-]+$/, 'nonce_format');

export const sendMessageSchema = z
  .object({
    // Looser than messageContentSchema: an attachment-only message (voice
    // note / image) legitimately has blank text. The superRefine below
    // enforces "must have text OR at least one upload".
    content: z.string().max(LIMITS.message.contentMax),
    replyToId: z.string().cuid().optional(),
    // IDs of Upload rows created via POST /api/uploads/complete.
    // Server verifies each ID belongs to the requesting user before writing.
    uploadIds: z.array(z.string().cuid()).max(LIMITS.attachment.perMessage).optional(),
    // Subset of uploadIds the user chose to "send as file" — the server flags
    // those attachments so the client renders them as a download chip even if
    // they're images.
    documentUploadIds: z.array(z.string().cuid()).max(LIMITS.attachment.perMessage).optional(),
    // Forwarding: re-reference an existing message's uploads. Not owner-scoped
    // (the forwarder rarely owns them), but each id must be an attachment of a
    // non-deleted message the forwarder can read.
    forwardUploadIds: z.array(z.string().cuid()).max(LIMITS.attachment.perMessage).optional(),
    // The message being forwarded: the server checks the uploads against it
    // directly. Optional for older clients (the server then looks the uploads
    // up among messages the forwarder can reach).
    forwardFromMessageId: z.string().cuid().optional(),
    // Advisory: server recomputes mentions from content. Sent purely so the
    // optimistic UI shows the same highlights server will end up applying.
    mentions: z.array(z.string().cuid()).max(50).optional(),
    clientNonce: clientNonceSchema.optional(),
  })
  .superRefine((val, ctx) => {
    const hasText = val.content.trim().length > 0;
    const hasUploads =
      (val.uploadIds?.length ?? 0) > 0 ||
      (val.documentUploadIds?.length ?? 0) > 0 ||
      (val.forwardUploadIds?.length ?? 0) > 0;
    if (!hasText && !hasUploads) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'content_blank', path: ['content'] });
    }
  });
export type SendMessageInput = z.infer<typeof sendMessageSchema>;

export const editMessageSchema = z.object({
  content: messageContentSchema,
  mentions: z.array(z.string().cuid()).max(50).optional(),
});
export type EditMessageInput = z.infer<typeof editMessageSchema>;

export const listMessagesQuerySchema = z.object({
  before: z.string().cuid().optional(),
  after: z.string().cuid().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type ListMessagesQuery = z.infer<typeof listMessagesQuerySchema>;

export const reactionSchema = z.object({
  emoji: z.string().min(1).max(64),
});
export type ReactionInput = z.infer<typeof reactionSchema>;

/**
 * What a service message (`Message.system`) records, as data rather than
 * text: each client renders it in its own language. `content` stays a short
 * language-neutral fallback for clients that don't know the event (and for
 * previews/search). Older service rows have no event — render `content`.
 */
export const messageSystemEventSchema = z.discriminatedUnion('kind', [
  z.object({
    /** The message author completed a task-list item. */
    kind: z.literal('task_done'),
    listId: z.string().min(1),
    itemId: z.string().min(1),
    label: z.string().min(1).max(LIMITS.task.labelMax),
  }),
]);
export type MessageSystemEvent = z.infer<typeof messageSystemEventSchema>;
